import {assert} from 'Asserts';
import {DataFrame, DataFrameColumnDef, DataFrameRow, isColumnTypeNumerical} from 'data-frames/DataFrameTypes';
import {ColumnsMapping, numericAccessor} from 'data-frames/util/AccessorUtils';
import aggregateRows from 'data-frames/util/aggregateRows';
import {createAccessors} from 'data-frames/util/createAccessors';
import findAndAssertColumn from 'data-frames/util/findAndAssertColumn';
import {LeafType} from 'data-types/benchling-type-def/BenchlingTypeDef';
import {
  AnalyticsVisualizationAggregationType as AggregationType,
  AnalyticsVisualizationErrorBarType as ErrorBarType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import pickDefined from 'util/pickDefined';

import {AGGREGATION_TYPE_TO_FUNCTION, ERROR_BAR_TYPE_TO_ERROR_VALUE_FUNCTION} from './Constants';
import getAggregatedAxisName from './getAggregatedAxisName';

export const UNEXPECTED_COLUMN_TYPE_ERROR =
  'Expected column type to be numerical if aggregationType is not COUNT or errorBarTypeIsSpecified';

export const AGGREGATED_VALUES_COLUMN_DEF: Omit<DataFrameColumnDef, 'displayName'> = {
  systemName: 'aggregated_values',
  type: {
    leafType: LeafType.DECIMAL,
  },
};

export const ERROR_BAR_VALUES_COLUMN_DEF: DataFrameColumnDef = {
  systemName: 'error_bar_values',
  displayName: 'Error bar values',
  type: {
    leafType: LeafType.DECIMAL,
    isNullable: true,
  },
};

/**
 * Aggregate data frame for charts that support aggregated columns and error bars.
 *
 * The input specifies 2-3 columns to perform the aggregation:
 *  - `partitionColumn`: The column to group values with. The output will have one row per unique value in this column.
 *  - `secondaryPartitionColumn`: (Optional) Adds a second column to group values with. If specified, the output
 *    will have one row per unique `partitionColumn` and `secondaryPartitionColumn` value pair.
 *  - `valueColumn`: The column to use to generate the new aggregated values and error bar values in the output.
 *
 * We can then specify up to two aggregation types to generate aggregated values (with `aggregationType`) and
 * optional, error bar values (with `errorBarType`).
 *
 * The output will have 2-4 columns:
 *  - `partitionColumn`: The same as the input column but with only unique values
 *  - `secondaryPartitionColumn`: Only included if it was included in the input. It's the same as the input, but
 *     there will only be unique `partitionColumn` and `secondaryPartitionColumn` pairs in the output.
 *  - `aggregatedValues`: The aggregated values for each partition
 *  - `errorBarValues`: The error bar values for each partition. Only included If `errorBarType` was specified.
 *
 */
export function aggregateChartDataFrame(
  dataFrame: DataFrame,
  columnsMapping: ColumnsMapping<'partitionColumn' | 'valueColumn', 'secondaryPartitionColumn'>,
  aggregationType: AggregationType,
  errorBarType: ErrorBarType | null = null
): {
  columnsMapping: ColumnsMapping<
    'partitionColumn' | 'aggregatedValues',
    'errorBarValues' | 'secondaryPartitionColumn'
  >;
  dataFrame: DataFrame;
} {
  const partitionColumn = findAndAssertColumn(dataFrame, columnsMapping.partitionColumn);
  const secondaryPartitionColumn =
    columnsMapping.secondaryPartitionColumn == null
      ? null
      : findAndAssertColumn(dataFrame, columnsMapping.secondaryPartitionColumn);
  const valueColumn = findAndAssertColumn(dataFrame, columnsMapping.valueColumn);

  const {
    accessors: {
      partitionColumn: partitionAccessor,
      secondaryPartitionColumn: secondaryPartitionAccessor,
      ...accessors
    },
    validatedLeafTypes,
  } = createAccessors(dataFrame, columnsMapping);

  assert(
    (aggregationType === AggregationType.COUNT && errorBarType == null) ||
      isColumnTypeNumerical(valueColumn.type),
    UNEXPECTED_COLUMN_TYPE_ERROR
  );

  const valueAccessor =
    aggregationType === AggregationType.COUNT
      ? () => 1
      : numericAccessor(accessors.valueColumn, validatedLeafTypes.valueColumn);

  const aggregateValuesFn = AGGREGATION_TYPE_TO_FUNCTION[aggregationType];
  const errorBarValuesFn = errorBarType && ERROR_BAR_TYPE_TO_ERROR_VALUE_FUNCTION[errorBarType];

  return {
    dataFrame: aggregateRows(
      dataFrame,
      (row: DataFrameRow) =>
        secondaryPartitionAccessor == null
          ? String(partitionAccessor(row))
          : `${String(partitionAccessor(row))} - ${String(secondaryPartitionAccessor(row))}`,
      (rows: Array<DataFrameRow>) => {
        assert(rows.length > 0, 'Expected partition to be non-empty');
        const values = rows.map((row) => valueAccessor(row));
        const aggregatedValues = aggregateValuesFn(values);
        const errorValue = errorBarValuesFn && errorBarValuesFn(values);

        return {
          [columnsMapping.partitionColumn]: partitionAccessor(rows[0]),
          ...(secondaryPartitionAccessor &&
            columnsMapping.secondaryPartitionColumn && {
              [columnsMapping.secondaryPartitionColumn]: secondaryPartitionAccessor(rows[0]),
            }),
          [AGGREGATED_VALUES_COLUMN_DEF.systemName]: String(aggregatedValues),
          ...(errorBarType && {
            [ERROR_BAR_VALUES_COLUMN_DEF.systemName]: errorValue == null ? null : String(errorValue),
          }),
        };
      },
      [
        partitionColumn,
        ...(secondaryPartitionColumn == null ? [] : [secondaryPartitionColumn]),
        {
          ...AGGREGATED_VALUES_COLUMN_DEF,
          displayName: getAggregatedAxisName(aggregationType, valueColumn.displayName),
        },
        ...(errorBarType == null ? [] : [ERROR_BAR_VALUES_COLUMN_DEF]),
      ]
    ),
    columnsMapping: {
      ...pickDefined(columnsMapping, 'partitionColumn', 'secondaryPartitionColumn'),
      aggregatedValues: AGGREGATED_VALUES_COLUMN_DEF.systemName,
      ...(errorBarType != null
        ? {
            errorBarValues: ERROR_BAR_VALUES_COLUMN_DEF.systemName,
          }
        : {}),
    },
  };
}
